// src/components/admin/AdminAnalytics.jsx
import { useEffect, useState } from 'react';
import '../../styles/AdminAnalytics.css'; // optionnel, tu peux en créer un plus tard

export default function AdminAnalytics() {
  const [summary, setSummary] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');
  const [pages, setPages] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');

  // Chargement du résumé (30 derniers jours)
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoadingSummary(true);
        setError('');

        const res = await fetch('/api/admin/analytics/summary', {
          credentials: 'include',
        });

        if (!res.ok) {
          throw new Error(`Erreur serveur: ${res.status}`);
        }

        const data = await res.json();

        if (!data.success) {
          throw new Error(data.message || 'Erreur inconnue');
        }

        setSummary(data.data || []);

        // Pré-sélectionne le dernier jour dispo (le plus récent)
        if (data.data && data.data.length > 0) {
          setSelectedDay(data.data[0].day);
        }
      } catch (err) {
        console.error(err);
        setError(err.message || "Erreur lors du chargement du résumé.");
      } finally {
        setLoadingSummary(false);
      }
    };

    fetchSummary();
  }, []);

  // Chargement des détails (pages + pays) à chaque changement de date
  useEffect(() => {
    if (!selectedDay) return;

    const fetchDetail = async () => {
      try {
        setLoadingDetail(true);
        setError('');

        const [pagesRes, countriesRes] = await Promise.all([
          fetch(`/api/admin/analytics/pages?day=${encodeURIComponent(selectedDay)}`, {
            credentials: 'include',
          }),
          fetch(`/api/admin/analytics/countries?day=${encodeURIComponent(selectedDay)}`, {
            credentials: 'include',
          }),
        ]);

        if (!pagesRes.ok || !countriesRes.ok) {
          throw new Error('Erreur serveur lors du chargement des détails.');
        }

        const pagesJson = await pagesRes.json();
        const countriesJson = await countriesRes.json();

        if (!pagesJson.success) {
          throw new Error(pagesJson.message || 'Erreur pages');
        }
        if (!countriesJson.success) {
          throw new Error(countriesJson.message || 'Erreur pays');
        }

        setPages(pagesJson.data || []);
        setCountries(countriesJson.data || []);
      } catch (err) {
        console.error(err);
        setError(err.message || "Erreur lors du chargement des détails.");
      } finally {
        setLoadingDetail(false);
      }
    };

    fetchDetail();
  }, [selectedDay]);

  return (
    <div className="admin-analytics">
      <h2>Analytics du site</h2>

      {error && <div className="admin-analytics-error">{error}</div>}

      {/* Résumé par jour */}
      <section className="analytics-section">
        <h3>Résumé des visites (30 derniers jours)</h3>

        {loadingSummary ? (
          <p>Chargement du résumé...</p>
        ) : summary.length === 0 ? (
          <p>Aucune donnée disponible pour le moment.</p>
        ) : (
          <div className="analytics-summary-wrapper">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Jour</th>
                  <th>Hits</th>
                  <th>Visiteurs uniques</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((row) => (
                  <tr
                    key={row.day}
                    className={row.day === selectedDay ? 'selected-row' : ''}
                    onClick={() => setSelectedDay(row.day)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{row.day}</td>
                    <td>{row.total_hits}</td>
                    <td>{row.unique_visitors}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="analytics-day-selector">
              <label>
                Jour sélectionné :
                <select
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(e.target.value)}
                >
                  {summary.map((row) => (
                    <option key={row.day} value={row.day}>
                      {row.day}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}
      </section>

      {/* Détails pour le jour sélectionné */}
      <section className="analytics-section">
        <h3>Détails pour le {selectedDay || '...'}</h3>

        {loadingDetail ? (
          <p>Chargement des détails...</p>
        ) : (
          <div className="analytics-detail-grid">
            {/* Top pages */}
            <div className="analytics-card">
              <h4>Top pages</h4>
              {pages.length === 0 ? (
                <p>Aucune donnée.</p>
              ) : (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Page</th>
                      <th>Hits</th>
                      <th>Visiteurs uniques</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pages.map((p) => (
                      <tr key={p.path}>
                        <td className="path-cell">{p.path}</td>
                        <td>{p.hits}</td>
                        <td>{p.unique_visitors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Top pays */}
            <div className="analytics-card">
              <h4>Pays</h4>
              {countries.length === 0 ? (
                <p>Aucune donnée.</p>
              ) : (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Pays</th>
                      <th>Hits</th>
                      <th>Visiteurs uniques</th>
                    </tr>
                  </thead>
                  <tbody>
                    {countries.map((c) => (
                      <tr key={c.country_code}>
                        <td>{c.country_code}</td>
                        <td>{c.hits}</td>
                        <td>{c.unique_visitors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
